import { useEffect, useState, useCallback } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { ConfirmDialog, Pagination } from '../../components';
import { staffTeamsService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { Team } from '../../lib/types';
import { Plus, Pencil, Trash2, X, Loader2, Users } from 'lucide-react';

type TeamForm = { name: string; color: string };
const EMPTY_FORM: TeamForm = { name: '', color: '#3b0b86' };

export default function ManageTeams() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<TeamForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await staffTeamsService.list({ page });
      setTeams(data.results);
      setTotalCount(data.count);
    } catch { setError('Eroare la încărcare.'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  function startAdd() { setIsAdding(true); setEditingId(null); setForm(EMPTY_FORM); setError(''); }
  function startEdit(t: Team) { setEditingId(t.id); setIsAdding(false); setForm({ name: t.name, color: t.color }); setError(''); }
  function cancel() { setIsAdding(false); setEditingId(null); setForm(EMPTY_FORM); setError(''); }

  async function handleSave() {
    if (!form.name.trim()) { setError('Numele este obligatoriu.'); return; }
    setSaving(true); setError('');
    try {
      if (isAdding) await staffTeamsService.create(form);
      else if (editingId) await staffTeamsService.update(editingId, form);
      cancel(); await load();
    } catch { setError('Eroare la salvare.'); }
    finally { setSaving(false); }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try { await staffTeamsService.delete(deleteTarget.id); setDeleteTarget(null); await load(); }
    catch { setError('Eroare la ștergere.'); }
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" /></div>;

  const formOpen = isAdding || editingId !== null;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Echipe</h1>
          <HelpButton to="/staff/documentatie/echipe" />
        </div>
        {!formOpen && (
          <button onClick={startAdd} className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--vivid-purple)]/90" style={{ fontFamily: 'var(--font-primary)' }}>
            <Plus className="h-4 w-4" /> Echipă Nouă
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {formOpen && (
        <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>{isAdding ? 'Echipă Nouă' : 'Editează Echipa'}</h2>
            <button onClick={cancel} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Nume *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Culoare</label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-10 cursor-pointer rounded border-0" />
                <input type="text" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} maxLength={7} className="w-28 rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)]" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={cancel} className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-gray-50">Anulează</button>
              <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60">
                {saving ? 'Se salvează...' : isAdding ? 'Adaugă' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="px-4 py-3 text-left font-semibold">Echipă</th>
              <th className="px-4 py-3 text-left font-semibold">Culoare</th>
              <th className="px-4 py-3 text-left font-semibold">Membri</th>
              <th className="px-4 py-3 text-right font-semibold">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                <td className="px-4 py-3 font-medium">{t.name}</td>
                <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="h-5 w-5 rounded-full border" style={{ backgroundColor: t.color }} /><span className="text-xs text-muted-foreground">{t.color}</span></div></td>
                <td className="px-4 py-3"><span className="flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" />{t.member_count}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => startEdit(t)} className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100 hover:text-[var(--vivid-purple)]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDeleteTarget(t)} className="rounded-lg p-2 text-muted-foreground hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {teams.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">Nicio echipă.</div>}
      </div>

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Șterge echipa"
        description={`Sigur vrei să ștergi echipa „${deleteTarget?.name}"? Acțiunea este ireversibilă.`}
      />
    </div>
  );
}
